import React, { useState } from 'react';
import { useAxiosAuth0 } from '@shared/config/useAxiosAuth0';
import GenericTabs from '@ui-components/Tabs/GenericTabs';
import GenericButton from '@ui-components/Button/GenericButton';
import { useFortiFlexData } from './hooks/useFortiFlexData';
import OverviewTab from './components/OverviewTab';
import AnalyticsTab from './components/AnalyticsTab';
import ManagementTab from './components/ManagementTab';

const PAGE_TABS = [
    { label: 'Overview', value: 'overview' },
    { label: 'Analytics', value: 'analytics' },
    { label: 'Management', value: 'management' },
];

function SkeletonBlock({ className }: { className?: string }) {
    return (
        <div
            className={`animate-pulse bg-semantic-bg-tertiary rounded-sm ${className ?? ''}`}
        />
    );
}

function OverviewSkeleton() {
    return (
        <div className="space-y-4">
            <SkeletonBlock className="h-12 w-full" />
            <SkeletonBlock className="h-10 w-full" />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {Array.from({ length: 4 }).map((_, i) => (
                    <SkeletonBlock key={i} className="h-24" />
                ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {Array.from({ length: 3 }).map((_, i) => (
                    <SkeletonBlock key={i} className="h-16" />
                ))}
            </div>
            <SkeletonBlock className="h-52 w-full" />
        </div>
    );
}

function AnalyticsSkeleton() {
    return (
        <div className="space-y-4">
            <SkeletonBlock className="h-8 w-48" />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {Array.from({ length: 4 }).map((_, i) => (
                    <SkeletonBlock key={i} className="h-20" />
                ))}
            </div>
            <SkeletonBlock className="h-72 w-full" />
            <SkeletonBlock className="h-64 w-full" />
        </div>
    );
}

function ManagementSkeleton() {
    return (
        <div className="space-y-8">
            {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="space-y-3">
                    <div className="flex items-center justify-between">
                        <SkeletonBlock className="h-5 w-32" />
                        <SkeletonBlock className="h-9 w-28" />
                    </div>
                    <SkeletonBlock className="h-10 w-full" />
                    {Array.from({ length: 3 }).map((__, j) => (
                        <SkeletonBlock key={j} className="h-10 w-full" />
                    ))}
                </div>
            ))}
        </div>
    );
}

const FortiFlexPage: React.FC = () => {
    const api = useAxiosAuth0();
    const {
        report,
        history,
        accountHistory,
        packs,
        accountLabels,
        alertRecipients,
        loading,
        refresh,
        refreshPacks,
        refreshLabels,
        refreshRecipients,
    } = useFortiFlexData(api);

    const [activeTab, setActiveTab] = useState('overview');
    const [refreshing, setRefreshing] = useState(false);

    const handleRefresh = async () => {
        setRefreshing(true);
        refresh();
        setTimeout(() => setRefreshing(false), 1500);
    };

    return (
        <div className="p-4 flex flex-col h-full">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h2 className="text-md font-bold text-semantic-text-primary">
                        FortiFlex Report
                    </h2>
                    <p className="text-xs text-semantic-text-secondary mt-0.5">
                        Points consumption monitoring and pack management
                    </p>
                </div>
                <GenericButton
                    variant="secondary"
                    size="md"
                    label={refreshing ? 'Refreshing...' : 'Refresh'}
                    onClick={handleRefresh}
                    loading={refreshing}
                    disabled={refreshing || loading}
                />
            </div>

            <GenericTabs
                tabs={PAGE_TABS}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                className="mb-4"
            />

            {loading && activeTab === 'overview' && <OverviewSkeleton />}
            {loading && activeTab === 'analytics' && <AnalyticsSkeleton />}
            {loading && activeTab === 'management' && <ManagementSkeleton />}

            {!loading && (
                <div className="flex-1 min-h-0">
                    {activeTab === 'overview' && (
                        report ? (
                            <OverviewTab report={report} accountLabels={accountLabels} />
                        ) : (
                            <p className="text-sm text-semantic-text-secondary py-8 text-center">
                                No report data available. Create a pack in the Management tab, then the scheduled job will generate reports automatically.
                            </p>
                        )
                    )}

                    {activeTab === 'analytics' && (
                        history.length ? (
                            <AnalyticsTab history={history} accountHistory={accountHistory} />
                        ) : (
                            <p className="text-sm text-semantic-text-secondary py-8 text-center">
                                No analytics data yet. Reports will appear here once the job runs.
                            </p>
                        )
                    )}

                    {activeTab === 'management' && (
                        <ManagementTab
                            packs={packs}
                            accountLabels={accountLabels}
                            alertRecipients={alertRecipients}
                            api={api}
                            onMutatePacks={refreshPacks}
                            onMutateLabels={refreshLabels}
                            onMutateRecipients={refreshRecipients}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default FortiFlexPage;
